import { useEffect, useLayoutEffect, useRef, useState, type FC } from 'react';
import type { JSONContent } from '@tiptap/react';
import { LuX, LuPlus, LuInfo, LuChevronLeft, LuArrowLeft, LuArrowRight } from 'react-icons/lu';
import Select from '../Select';
import FolhaDocumento from './FolhaDocumento';
import { useEmpresaDaFolha } from './useEmpresaDaFolha';
import { listarProntos, NOME_REQUISITO, type ModeloPronto } from '../../data/modelosDeDocumento';
import {
    NOME_TIPO_DOCUMENTO, OPCOES_TIPO_DOCUMENTO, TipoDocumento, type VariavelDocumento,
} from '../../services/ModeloDocumentoService';
import '../NovoAgenteModal.css';
import './NovoModeloModal.css';

/** O que a modal entrega para a página abrir o painel em edição. */
export interface EscolhaModelo {
    nome: string;
    tipo: TipoDocumento;
    validadeDias: number | null;
    ativo: boolean;
    conteudo: JSONContent;
}

const EM_BRANCO = '__branco';
type Filtro = 'todos' | TipoDocumento;

const FILTROS: { valor: Filtro; rotulo: string }[] = [
    { valor: 'todos', rotulo: 'Todos' },
    { valor: TipoDocumento.Proposta, rotulo: 'Proposta' },
    { valor: TipoDocumento.Orcamento, rotulo: 'Orçamento' },
    { valor: TipoDocumento.Contrato, rotulo: 'Contrato' },
];

/* Usar o mesmo pronto duas vezes esbarraria no "Já existe um modelo com esse
   nome" só no Salvar do painel. A sugestão já nasce livre. */
const nomeLivre = (base: string, existentes: string[]) => {
    const usados = new Set(existentes.map(n => n.trim().toLowerCase()));
    if (!usados.has(base.toLowerCase())) return base;
    let i = 2;
    while (usados.has(`${base} ${i}`.toLowerCase())) i++;
    return `${base} ${i}`;
};

const resumoDoPede = (m: ModeloPronto) =>
    m.pede.map(r => NOME_REQUISITO[r].replace(/^Pede /, '')).join(' e ');

/**
 * A folha em escala: a largura dela não muda (595, a do PDF, para quebrar a
 * linha igual), só o tamanho com que aparece. `max` é a escala desenhada; em
 * tela estreita ela cai até caber.
 */
const FolhaEscalada: FC<{ max: number; children: React.ReactNode }> = ({ max, children }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [escala, setEscala] = useState(max);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const medir = () => setEscala(Math.min(max, Math.max(0.2, (el.clientWidth - 8) / 595)));
        medir();
        const ro = new ResizeObserver(medir);
        ro.observe(el);
        return () => ro.disconnect();
    }, [max]);
    return (
        <div ref={ref} className="nmd-escala">
            <div className="nmd-escala-in" style={{ width: 595 * escala, height: 842 * escala }}>
                <div style={{ transform: `scale(${escala})`, transformOrigin: 'top left' }}>{children}</div>
            </div>
        </div>
    );
};

interface Props {
    aberta: boolean;
    /** Nomes dos modelos da empresa — para sugerir nome livre e barrar o repetido. */
    existentes: string[];
    variaveis: VariavelDocumento[];
    onFechar: () => void;
    /** `null` = folha em branco. */
    onEscolher: (escolha: EscolhaModelo | null) => void;
}

/**
 * NOVO MODELO DE DOCUMENTO — a galeria em dois passos do Novo agente e do
 * Novo trilho, Direção A da proposta modelos-de-documento-biblioteca.html
 * (aprovada em 05/10/2026).
 *
 * A casca é a do agente: as classes `na-` vêm de NovoAgenteModal.css de
 * propósito, para as três modais não divergirem. O que é só daqui (filtro por
 * tipo, miniatura da folha, passo 2 com a folha grande) mora em
 * NovoModeloModal.css, prefixo `nmd-`.
 *
 * A modal não cria nada: "Usar este modelo" entrega o rascunho para a página,
 * que abre o painel de sempre em edição. Só vira registro no Salvar dele.
 *
 * Sem hover — regra do produto. Seleção é borda + anel; foco tem marca própria.
 */
const NovoModeloModal: FC<Props> = ({ aberta, existentes, variaveis, onFechar, onEscolher }) => {
    const prontos = listarProntos();
    const [passo, setPasso] = useState<1 | 2>(1);
    const [filtro, setFiltro] = useState<Filtro>('todos');
    const [chave, setChave] = useState<string | null>(null);
    const [nome, setNome] = useState('');
    const [tipo, setTipo] = useState<TipoDocumento>(TipoDocumento.Proposta);
    const [validade, setValidade] = useState('');
    const [tentou, setTentou] = useState(false);
    const empresa = useEmpresaDaFolha(aberta);

    const refNome = useRef<HTMLInputElement | null>(null);
    const refPrimeiro = useRef<HTMLButtonElement | null>(null);

    const exemplos = Object.fromEntries(variaveis.map(v => [v.chave, v.exemplo]));
    const pronto = prontos.find(p => p.id === chave) ?? null;
    const emBranco = chave === EM_BRANCO;
    const visiveis = filtro === 'todos' ? prontos : prontos.filter(p => p.tipo === filtro);

    useEffect(() => {
        if (!aberta) return;
        setPasso(1); setFiltro('todos'); setChave(null); setNome(''); setValidade(''); setTentou(false);
        requestAnimationFrame(() => refPrimeiro.current?.focus());
    }, [aberta]);

    /* Esc no passo 1 fecha; no passo 2 volta, sem perder o que foi digitado. */
    useEffect(() => {
        if (!aberta) return;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (passo === 2) setPasso(1); else onFechar();
        };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [aberta, passo, onFechar]);

    useEffect(() => {
        if (aberta && passo === 2) requestAnimationFrame(() => refNome.current?.select());
    }, [aberta, passo]);

    if (!aberta) return null;

    const escolher = (k: string) => { setChave(k); setTentou(false); };

    const avancar = (k = chave) => {
        if (!k) return;
        /* A folha em branco não tem o que conferir: vai direto para o painel,
           como o "Novo modelo" fazia antes da biblioteca. */
        if (k === EM_BRANCO) { onEscolher(null); return; }
        const p = prontos.find(x => x.id === k);
        if (!p) return;
        setChave(k);
        setNome(nomeLivre(p.nome, existentes));
        setTipo(p.tipo);
        setValidade(p.validadeDias ? String(p.validadeDias) : '');
        setTentou(false);
        setPasso(2);
    };

    const texto = nome.trim();
    const repetido = !!texto && existentes.some(n => n.trim().toLowerCase() === texto.toLowerCase());
    const validadeNum = validade.trim() === '' ? null : Number(validade);
    const validadeRuim = validadeNum !== null && (!Number.isInteger(validadeNum) || validadeNum <= 0);
    const erroNome = tentou && (!texto || repetido);

    const usar = () => {
        setTentou(true);
        if (!pronto || !texto || repetido || validadeRuim) { refNome.current?.focus(); return; }
        /* Nasce inativo: os prontos têm trechos entre colchetes que só a empresa
           sabe, e colchete que chega ao cliente é defeito visível. */
        onEscolher({ nome: texto, tipo, validadeDias: validadeNum, ativo: false, conteudo: pronto.conteudo });
    };

    return (
        <div className="na-overlay" onClick={() => { if (passo === 1) onFechar(); }}>
            <div
                className="na nmd"
                role="dialog"
                aria-modal="true"
                aria-label={passo === 1 ? 'Novo modelo: escolher um ponto de partida' : `Novo modelo: ${pronto?.nome ?? ''}`}
                onClick={e => e.stopPropagation()}
            >
                <header className="na-head">
                    {passo === 2 && (
                        <button type="button" className="nmd-voltar" onClick={() => setPasso(1)} aria-label="Voltar para a galeria">
                            <LuArrowLeft size={14} />
                        </button>
                    )}
                    <div className="na-head-txt">
                        <h2 className="na-tit">{passo === 1 ? 'Novo modelo' : pronto?.nome}</h2>
                        <p className="na-sub">
                            {passo === 1
                                ? 'Comece por um pronto e ajuste o texto, ou parta da folha em branco.'
                                : `${NOME_TIPO_DOCUMENTO[pronto!.tipo]} · pede ${resumoDoPede(pronto!)} da oportunidade`}
                        </p>
                    </div>
                    <button type="button" className="na-x" onClick={onFechar} aria-label="Fechar">
                        <LuX size={16} />
                    </button>
                </header>

                {passo === 1 ? (
                    <div className="na-corpo">
                        <div className="nmd-filtros" role="group" aria-label="Filtrar por tipo">
                            {FILTROS.map(f => (
                                <button key={f.valor} type="button" aria-pressed={filtro === f.valor}
                                    className={`nmd-filtro ${filtro === f.valor ? 'is-on' : ''}`}
                                    onClick={() => setFiltro(f.valor)}>
                                    {f.rotulo}
                                </button>
                            ))}
                        </div>

                        <div className="na-gal" role="radiogroup" aria-label="Modelos prontos">
                            {visiveis.map((m, i) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={chave === m.id}
                                    ref={i === 0 ? refPrimeiro : undefined}
                                    className={`na-card nmd-card ${chave === m.id ? 'is-on' : ''}`}
                                    onClick={() => escolher(m.id)}
                                    onDoubleClick={() => avancar(m.id)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); avancar(m.id); } }}
                                >
                                    <span className="nmd-mini" aria-hidden="true">
                                        <span className="nmd-mini-folha">
                                            <FolhaDocumento conteudo={m.conteudo} tipo={m.tipo} validadeDias={m.validadeDias}
                                                empresa={empresa} exemplos={exemplos} />
                                        </span>
                                    </span>
                                    <span className="nmd-card-tx">
                                        <span className="na-card-tit">{m.nome}</span>
                                        <span className="na-card-txt">{NOME_TIPO_DOCUMENTO[m.tipo]} · {m.resumo}</span>
                                        <span className="na-card-chips">
                                            {m.pede.map(r => (
                                                <span key={r} className={`na-chip ${r === 'organizacao' ? 'nmd-chip-org' : ''}`}>{NOME_REQUISITO[r]}</span>
                                            ))}
                                        </span>
                                    </span>
                                </button>
                            ))}

                            {/* O branco fica em qualquer filtro: é sempre uma saída. */}
                            <button
                                type="button"
                                role="radio"
                                aria-checked={emBranco}
                                className={`na-card is-zero nmd-card ${emBranco ? 'is-on' : ''}`}
                                onClick={() => escolher(EM_BRANCO)}
                                onDoubleClick={() => avancar(EM_BRANCO)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); avancar(EM_BRANCO); } }}
                            >
                                <span className="nmd-mini is-branco" aria-hidden="true"><LuPlus size={20} /></span>
                                <span className="nmd-card-tx">
                                    <span className="na-card-tit">Em branco</span>
                                    <span className="na-card-txt">Folha vazia, para escrever do zero.</span>
                                </span>
                            </button>
                        </div>
                    </div>
                ) : pronto && (
                    <div className="na-corpo nmd-p2">
                        <div className="nmd-p2-esq">
                            <div className="na-campo">
                                <label className="na-lab" htmlFor="nmd-nome">Nome</label>
                                <input
                                    id="nmd-nome"
                                    ref={refNome}
                                    type="text"
                                    maxLength={150}
                                    className={`na-inp ${erroNome ? 'is-erro' : ''}`}
                                    value={nome}
                                    onChange={e => setNome(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') usar(); }}
                                />
                                {erroNome && (
                                    <span className="na-hint is-erro">
                                        {!texto ? 'O modelo precisa de um nome.' : 'Já existe um modelo com esse nome.'}
                                    </span>
                                )}
                            </div>
                            <div className="nmd-dois">
                                <div className="na-campo">
                                    <span className="na-lab">Tipo</span>
                                    <Select value={String(tipo)} options={OPCOES_TIPO_DOCUMENTO} portal ariaLabel="Tipo"
                                        onChange={v => setTipo(Number(v) as TipoDocumento)} />
                                </div>
                                <div className="na-campo">
                                    <label className="na-lab" htmlFor="nmd-validade">Validade (dias)</label>
                                    <input id="nmd-validade" type="number" min={1} placeholder="Sem validade"
                                        className={`na-inp ${tentou && validadeRuim ? 'is-erro' : ''}`}
                                        value={validade} onChange={e => setValidade(e.target.value)} />
                                </div>
                            </div>
                            {tentou && validadeRuim && <span className="na-hint is-erro">Use um número de dias maior que zero, ou deixe em branco.</span>}

                            <p className="nmd-nota">
                                O texto vem pronto e abre no editor para você ajustar. Os trechos em azul são preenchidos a cada oportunidade.
                            </p>
                            <p className="nmd-nota">
                                O modelo nasce <b>inativo</b>: ele só aparece na hora de emitir depois que você revisar e ligar.
                            </p>
                            {pronto.textoBase && (
                                <p className="na-aviso nmd-aviso">
                                    <LuInfo size={13} />
                                    <span>Texto-base. Revise com quem cuida do jurídico da sua empresa.</span>
                                </p>
                            )}
                        </div>
                        <div className="nmd-palco" aria-label={`Prévia de ${pronto.nome} com dados de exemplo`}>
                            <FolhaEscalada max={0.86}>
                                <FolhaDocumento conteudo={pronto.conteudo} tipo={tipo}
                                    validadeDias={validadeRuim ? null : validadeNum}
                                    empresa={empresa} exemplos={exemplos} />
                            </FolhaEscalada>
                        </div>
                    </div>
                )}

                <footer className="na-pe">
                    <span className="na-passo">{passo === 1 ? `Passo 1 de 2 · ${prontos.length} modelos` : 'Passo 2 de 2'}</span>
                    {passo === 1 ? (
                        <>
                            <button type="button" className="na-btn" onClick={onFechar}>Cancelar</button>
                            <button type="button" className="na-btn is-primary" disabled={!chave} onClick={() => avancar()}>
                                Continuar <LuArrowRight size={14} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="na-btn" onClick={() => setPasso(1)}>
                                <LuChevronLeft size={14} /> Voltar
                            </button>
                            <button type="button" className="na-btn is-primary" onClick={usar}>Usar este modelo</button>
                        </>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default NovoModeloModal;
